<template>
  <WidgetConfigsHeader
    v-if="simpleMode || componentOrientation == 'landscape'"
    class="pt-3"
    :class="{ 'border-b border-gray-800 px-4 pb-7': !simpleMode }"
    v-model:title="editableWidget.title"
    v-model:description="editableWidget.description"
  />
  <div class="flex h-full w-full items-center overflow-y-auto">
    <div v-if="componentToDisplay" :class="['flex h-full flex-col', simpleMode ? 'w-full' : 'w-[60%]']">
      <WidgetConfigsHeader
        v-if="!simpleMode && componentOrientation == 'portrait'"
        class="border-r border-gray-800 px-4 pt-3"
        v-model:title="editableWidget.title"
        v-model:description="editableWidget.description"
      />
      <div
        class="overflow-y-auto"
        :class="[simpleMode ? 'h-[350px]' : 'h-full w-full border-r border-gray-800 px-4 pt-5']"
      >
        <component
          :key="editableWidget.component"
          :is="componentToDisplay"
          :simple-mode="simpleMode"
          :config="widgetConfigurations"
          :created-by="editableWidget.widget_template.user_id"
          :template-id="editableWidget.uuid || editableWidget.widget_template_id"
          :widget-id="editableWidget.id"
          @update="handleConfigUpdate"
          @update:channels="emits('update:channels', $event)"
        />
      </div>
    </div>
    <div
      v-if="!simpleMode"
      :class="['h-full px-3 py-3', componentOrientation == 'portrait' ? 'w-[40%]' : 'w-full overflow-x-auto']"
    >
      <slot name="preview"></slot>
    </div>
  </div>
</template>
<script setup>
import WidgetConfigsIframe from '@/components/widget_configs/WidgetConfigsIframe.vue';
import WidgetConfigsCalendar from '@/components/widget_configs/WidgetConfigsCalendar.vue';
import WidgetConfigsCoinProfile from '@/components/widget_configs/WidgetConfigsCoinProfile.vue';
import WidgetConfigsDexFilters from '@/components/widget_configs/WidgetConfigsDexFilters.vue';
import WidgetConfigsNews from '@/components/widget_configs/WidgetConfigsNews.vue';
import WidgetConfigsCustomComponent from '@/components/widget_configs/WidgetConfigsCustomComponent.vue';
import WidgetConfigsScreener from '@/components/widget_configs/WidgetConfigsScreener.vue';
import WidgetsConfigsResearchChart from '@/components/widget_configs/WidgetsConfigsResearchChart.vue';
import WidgetConfigsMarkdown from '@/components/widget_configs/WidgetConfigsMarkdown.vue';
import WidgetConfigsDivider from '@/components/widget_configs/WidgetConfigsDivider.vue';
import WidgetConfigsDerivatives from '@/components/widget_configs/WidgetConfigsDerivatives.vue';
import WidgetConfigsTradingviewChart from '@/components/widget_configs/WidgetConfigsTradingviewChart.vue';
import WidgetConfigsGovernance from '@/components/widget_configs/WidgetConfigsGovernance.vue';
import WidgetConfigsCoinUnlock from '@/components/widget_configs/WidgetConfigsCoinUnlock.vue';
import WidgetConfigsTradingviewEconomicCalendar from '@/components/widget_configs/WidgetConfigsTradingviewEconomicCalendar.vue';
import WidgetConfigsHeader from '@/components/widget_configs/WidgetConfigsHeader.vue';
import { ref, computed, watch, onBeforeUnmount, onBeforeUpdate } from 'vue';

const emits = defineEmits(['update', 'update:channels']);
const props = defineProps({
  widget: { type: Object, required: false },
  simpleMode: { type: Boolean, default: true }
});

//CONFIG COMPONENTS
const widgetConfigComponents = {
  WidgetsSimpleIframe: { component: WidgetConfigsIframe, orientation: 'portrait' },
  WidgetsCalendar: { component: WidgetConfigsCalendar, orientation: 'portrait' },
  WidgetsCoinProfile: { component: WidgetConfigsCoinProfile, orientation: 'portrait' },
  WidgetsDexTrades: { component: WidgetConfigsDexFilters, orientation: 'landscape' },
  WidgetsDexTransactions: { component: WidgetConfigsDexFilters, orientation: 'landscape' },
  WidgetsNews: { component: WidgetConfigsNews, orientation: 'portrait' },
  WidgetsCustomComponent: { component: WidgetConfigsCustomComponent, orientation: 'landscape' },
  WidgetsScreener: { component: WidgetConfigsScreener, orientation: 'portrait' },
  WidgetsMarkdown: { component: WidgetConfigsMarkdown, orientation: 'portrait' },
  WidgetsDivider: { component: WidgetConfigsDivider, orientation: 'portrait' },
  WidgetsDerivatives: { component: WidgetConfigsDerivatives, orientation: 'landscape' },
  WidgetsTradingviewChart: { component: WidgetConfigsTradingviewChart, orientation: 'landscape' },
  WidgetsResearchChart: { component: WidgetsConfigsResearchChart, orientation: 'landscape' },
  WidgetsGovernanceProposals: { component: WidgetConfigsGovernance, orientation: 'landscape' },
  WidgetsCoinUnlock: { component: WidgetConfigsCoinUnlock, orientation: 'landscape' },
  WidgetsTradingviewEconomicCalendar: { component: WidgetConfigsTradingviewEconomicCalendar, orientation: 'landscape' }
};
const componentToDisplay = computed(() => {
  return widgetConfigComponents[editableWidget.value.component]?.component;
});

const componentOrientation = computed(() => {
  return widgetConfigComponents[editableWidget.value.component]?.orientation || 'landscape';
});

//EDITABLE WIDGET
const editableWidget = ref(JSON.parse(JSON.stringify(props.widget)));

const widgetConfigurations = computed(() => {
  let defaultConfig = { ...editableWidget.value.widget_template.default_configuration };
  let userConfig = { ...editableWidget.value.configuration };
  let updatedConfig = { ...defaultConfig, ...userConfig };

  if (editableWidget.value.component == 'WidgetsDerivatives' && userConfig) {
    updatedConfig = userConfig;
  }
  if (editableWidget.value.component == 'WidgetsCustomComponent' && editableWidget.value.id && userConfig) {
    //editing in ModalEditWidget component
    updatedConfig.source = userConfig.source;
  }
  return updatedConfig;
});

watch(
  () => editableWidget.value.title,
  () => {
    emits('update', editableWidget.value);
  }
);
watch(
  () => editableWidget.value.description,
  () => {
    emits('update', editableWidget.value);
  }
);
// watch(
//   () => editableWidget.value.configuration,
//   (oldVal, newVal) => {
//     if (!isEqual(oldVal, newVal)) {
//       console.log('WATCH UPDATE');
//       emits('update', editableWidget.value);
//     }
//   },
//   { deep: true, immediate: true }
// );

function handleConfigUpdate(payload) {
  editableWidget.value.configuration = payload;
  emits('update', editableWidget.value);
}

onBeforeUnmount(() => {
  Object.assign(editableWidget.value, {});
});
onBeforeUpdate(() => {
  if (!componentToDisplay.value) {
    //force valid config if no custom configs are available
    editableWidget.value.configuration = {
      ...editableWidget.value.configuration,
      valid: true
    };
  }
});
</script>
